<template>
    <v-dialog :value="value" max-width="520" scrollable persistent content-class="nrq-dialog"
        @input="$emit('input', $event)">
        <v-card class="nrq-card">
            <v-card-title class="py-3 px-4 flex-shrink-0">
                <span class="text-h6 font-weight-bold d-flex align-center">
                    <v-icon left :color="isWfh ? 'green darken-2' : 'primary'">
                        {{ isWfh ? 'mdi-home-account' : 'mdi-calendar-heart' }}
                    </v-icon>
                    New Request
                </span>
                <v-spacer />
                <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>
            <v-divider />

            <v-card-text class="pt-4 nrq-body">
                <!-- Kind -->
                <div class="text-caption font-weight-bold grey--text text--darken-1 mb-1">TYPE</div>
                <v-btn-toggle v-model="kind" mandatory dense color="primary" class="mb-4">
                    <v-btn value="leave" small class="text-none">Leave</v-btn>
                    <v-btn value="wfh" small class="text-none">
                        <v-icon x-small left>mdi-home-account</v-icon>
                        Work From Home
                    </v-btn>
                </v-btn-toggle>

                <v-row dense>
                    <v-col v-if="!isWfh" cols="12">
                        <v-combobox v-model="leaveTypeId" :items="selectableLeaveTypes" item-id="id" item-text="name"
                            label="Leave type" outlined dense hide-details></v-combobox>
                    </v-col>
                    <v-col cols="12">
                        <v-combobox v-model="approverId" :items="approverOptions" item-id="id" item-text="approver_label"
                            label="Approver" outlined dense hide-details></v-combobox>
                    </v-col>
                </v-row>

                <!-- Duration -->
                <div class="text-caption font-weight-bold grey--text text--darken-1 mt-4 mb-1">DURATION</div>
                <v-btn-toggle v-model="durationType" mandatory dense color="primary">
                    <v-btn value="full" small class="text-none">Full Day</v-btn>
                    <v-btn value="half" small class="text-none">Half Day</v-btn>
                </v-btn-toggle>

                <v-fade-transition>
                    <div v-if="durationType === 'half'" class="mt-2">
                        <div class="text-caption font-weight-bold orange--text text--darken-2 mb-1">PERIOD</div>
                        <v-btn-toggle v-model="halfDayPeriod" mandatory dense color="orange">
                            <v-btn value="AM" small class="text-none">Morning (AM)</v-btn>
                            <v-btn value="PM" small class="text-none">Afternoon (PM)</v-btn>
                        </v-btn-toggle>
                    </div>
                </v-fade-transition>

                <v-row dense class="mt-3">
                    <v-col cols="12" sm="6">
                        <v-text-field :value="fromDate" label="From" prepend-inner-icon="mdi-calendar" readonly outlined
                            dense hide-details></v-text-field>
                    </v-col>
                    <v-col v-if="durationType === 'full'" cols="12" sm="6">
                        <v-menu v-model="toMenu" :close-on-content-click="false" offset-y min-width="auto">
                            <template #activator="{ on, attrs }">
                                <v-text-field :value="toDate" label="To" prepend-inner-icon="mdi-calendar" readonly
                                    v-bind="attrs" v-on="on" outlined dense hide-details></v-text-field>
                            </template>
                            <v-date-picker v-model="toDate" :min="fromDate" @input="toMenu = false"></v-date-picker>
                        </v-menu>
                    </v-col>
                </v-row>

                <v-alert v-if="count !== null" dense outlined class="mt-3 mb-0" type="info">
                    <span class="text-caption">
                        <strong>Duration:</strong>
                        {{ durationType === 'half' ? `0.5 day (${halfDayPeriod || 'select AM/PM'})` : `${count} day${count === 1 ? '' : 's'}` }}
                    </span>
                </v-alert>

                <v-textarea v-model="note" label="Note" outlined dense rows="3" hide-details class="mt-3"></v-textarea>
            </v-card-text>

            <v-divider />
            <v-card-actions class="px-4 py-3 flex-shrink-0">
                <v-spacer />
                <v-btn text @click="close">Cancel</v-btn>
                <v-btn :color="isWfh ? 'green darken-2' : 'primary'" depressed class="white--text text-none"
                    :disabled="!isValid || submitting" :loading="submitting" @click="submit">
                    <v-icon small left>mdi-send</v-icon>
                    Send Request
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import dayjs from 'dayjs';
import { getCompanyManagerApprovers } from '@/components/profile/leaveOvertimeUtils';

const WFH_TYPE_NAME = 'Work From Home';

export default {
    name: 'NewRequestDialog',
    props: {
        value: { type: Boolean, default: false },
        // Pre-selected date, YYYY-MM-DD, from the clicked calendar cell.
        date: { type: String, default: null },
        leaveTypes: { type: Array, default: () => [] },
        // Candidate approvers (the full users list; the current user is filtered out).
        users: { type: Array, default: () => [] },
        currentUser: { type: Object, default: null },
        roleManagers: { type: Array, default: () => [] },
        roles: { type: Array, default: () => [] },
    },
    data() {
        return {
            kind: 'leave', // 'leave' | 'wfh'
            leaveTypeId: null,
            approverId: null,
            durationType: 'full', // 'full' | 'half'
            halfDayPeriod: 'AM',
            toDate: null,
            toMenu: false,
            note: '',
            submitting: false,
        };
    },
    computed: {
        isWfh() {
            return this.kind === 'wfh';
        },
        fromDate() {
            return this.date;
        },
        wfhType() {
            return this.leaveTypes.find((t) => (t.name || '').trim().toLowerCase() === WFH_TYPE_NAME.toLowerCase()) || null;
        },
        selectableLeaveTypes() {
            return this.leaveTypes.filter((t) => (t.name || '').trim().toLowerCase() !== WFH_TYPE_NAME.toLowerCase());
        },
        approverOptions() {
            return getCompanyManagerApprovers(this.users, this.currentUser, this.roleManagers, this.roles);
        },
        // Calendar days (weekends included) in the selected range, or 0.5 for a half day.
        count() {
            if (this.durationType === 'half') return 0.5;
            const from = dayjs(this.fromDate);
            const to = dayjs(this.toDate || this.fromDate);
            if (!from.isValid() || !to.isValid() || to.isBefore(from, 'day')) return null;
            return to.startOf('day').diff(from.startOf('day'), 'day') + 1;
        },
        resolvedTypeId() {
            if (this.isWfh) return this.wfhType && this.wfhType.id;
            const chosen = this.leaveTypeId;
            if (!chosen) return null;
            return typeof chosen === 'object' ? chosen.id : chosen;
        },
        resolvedApproverId() {
            const chosen = this.approverId;
            if (!chosen) return null;
            return typeof chosen === 'object' ? chosen.id : chosen;
        },
        isValid() {
            return !!(
                this.fromDate &&
                this.resolvedTypeId &&
                this.resolvedApproverId &&
                this.note &&
                this.note.trim() &&
                this.count &&
                (this.durationType !== 'half' || this.halfDayPeriod)
            );
        },
    },
    watch: {
        value(open) {
            if (open) {
                this.reset();
            }
        },
        date() {
            this.toDate = this.date;
        },
    },
    methods: {
        reset() {
            this.kind = 'leave';
            this.leaveTypeId = null;
            this.approverId = null;
            this.durationType = 'full';
            this.halfDayPeriod = 'AM';
            this.toDate = this.date;
            this.note = '';
            this.submitting = false;
        },
        close() {
            this.$emit('input', false);
        },
        async submit() {
            if (!this.isValid || this.submitting) return;
            this.submitting = true;
            const isHalf = this.durationType === 'half';
            const payload = {
                leave_type_id: this.resolvedTypeId,
                approver_id: this.resolvedApproverId,
                count: this.count,
                from: this.fromDate,
                to: isHalf ? this.fromDate : (this.toDate || this.fromDate),
                note: this.note.trim(),
                is_half_day: isHalf,
                half_day_period: isHalf ? this.halfDayPeriod : null,
            };
            try {
                const { data } = await this.$axios.post('/api/leave/request', payload);
                if (data && data.response === false) {
                    this.$toast.error(data.message || 'Could not submit the request.');
                    return;
                }
                this.$toast.success(this.isWfh ? 'Work from home request submitted' : 'Leave request submitted');
                this.$emit('created');
                this.$emit('input', false);
            } catch (e) {
                this.$toast.error(e?.response?.data?.message || 'Could not submit the request.');
            } finally {
                this.submitting = false;
            }
        },
    },
};
</script>

<style>
/* Not scoped: the dialog renders at the app root, outside this subtree.
   `content-class="nrq-dialog"` lands on the inner `.v-dialog` element here. */
.nrq-dialog.v-dialog--scrollable {
    max-height: 90vh;
}

/* Make the card body the single scroll region (reinforces `scrollable`). */
.nrq-dialog .v-card__text.nrq-body {
    flex: 1 1 auto;
    overflow-y: auto;
    min-height: 0;
}
</style>
